import $ from "jquery";
import "jquery-ui";
import "jquery-ui/ui/widgets/dialog";
import ko from "knockout";
import componentCache from "flow/utils/componentCache";
import deferred, { JQueryDeferredPlus } from "flow/promise/deferred";
import config from "flow/config";
import "flow/viewModels/ErrorDialog";
import "flow/viewModels/InformationDialog";
import "flow/viewModels/ConfirmDialog";
import "flow/viewModels/SystemInformationDialog";
import "flow/viewModels/ProductSelectorDialog";
import ajaxErrorHelper from "flow/ajax/ajaxErrorHelper";
import notificationView from "./dialogNotification.html";
import { Dictionary } from "flow/api/types";
import MyFlow from "flow/MyFlow";
import Component from "flow/Component";
import buttonPaneHtml from "../viewModels/ButtonPane.html";

const protectingView = '<div data-bind="stopBinding: true"></div>';
const contentView = '<div class="stretch" data-bind="classIdentifierDialog: $data"></div>';

const dialogClass = "uiDialog";

export interface IDimension {
    height: number | string;
    width: number | string;
}

export class Dialog {
    dialogStack = ko.observableArray([]);

    currentComponent = ko.pureComputed(() => {
        if (this.dialogStack().length > 0) {
            return this.dialogStack()[this.dialogStack().length - 1];
        }
        return undefined;
    });

    currentId = ko.pureComputed(() => {
        const comp = this.currentComponent();
        if (comp) {
            return comp.manifest.id;
        }
        return "";
    });

    getDialogClass(isLoading: boolean): string {
        return isLoading ? dialogClass + " lc-loading-layer" : dialogClass;
    }

    onIsLoadingChanged(protectingElement: JQuery<HTMLElement>, isLoading: boolean): void {
        protectingElement.dialog("option", "classes.ui-dialog", this.getDialogClass(isLoading));
    }

    showCustomDialog<T>(myFlow: MyFlow, id: string, viewContext: Dictionary): JQueryPromise<T> {
        const dfd = deferred<T>(myFlow);
        const component = componentCache.getComponentById(id);

        const loadingPromise = component
            .load()
            .done(() => {
                const $protectingElement = $(protectingView);
                const $contentElement = $(contentView);

                $protectingElement.append($contentElement);

                $contentElement.html(component.viewHtml());
                component.setElement($contentElement.children().first());

                // Perform component instance specific subscriptions
                const loadingSubscriber = component.isLoading.subscribe(newValue => {
                    this.onIsLoadingChanged($protectingElement, newValue);
                });

                const titleSubscriber = component.title.subscribe(newTitle => {
                    $protectingElement.dialog("option", "title", newTitle);
                });

                const sizeSubscriber = () => {
                    this.reevaluateDimension($protectingElement, component);
                };
                $(window).on("resize", sizeSubscriber);

                $protectingElement
                    .dialog({
                        classes: {
                            "ui-dialog": this.getDialogClass(component.isLoading()),
                            "ui-dialog-titlebar": component.manifest.titleClasses
                        },
                        width: "auto",
                        height: "auto",
                        modal: true,
                        resizable: false,
                        title: component.title(),
                        close: () => {
                            this.onClose(
                                dfd,
                                $protectingElement,
                                loadingSubscriber,
                                titleSubscriber,
                                component,
                                sizeSubscriber
                            );
                        },
                        position: { my: "center", at: "center", of: window }
                    })
                    .dialog("open");

                this.dialogStack.push(component);
                if (component.useValidationV3()) {
                    ko.applyBindingsWithValidation(
                        component.viewModel(),
                        $contentElement[0],
                        component.validationOptionsV3()
                    );
                } else {
                    ko.applyBindings(component.viewModel(), $contentElement[0]);
                }
                component.onViewAttached($contentElement.children()[0]);

                // Add custom button pane
                $contentElement.closest(".ui-dialog").find(".ui-dialog-buttonpane").remove();
                $contentElement.closest(".ui-dialog").append(buttonPaneHtml);
                ko.applyBindings(component, $contentElement.closest(".ui-dialog").find(".ui-dialog-buttonset")[0]);

                //Add notification pane
                $protectingElement.parent().find(".lc-notification").remove();
                $(notificationView).insertAfter($protectingElement.parent().find(".ui-widget-header"));
                ko.applyBindings(component, $protectingElement.parent().find(".lc-notification")[0]);

                component.activate(
                    viewContext,
                    (data?: any) => {
                        this.close(dfd, $protectingElement, data);
                    },
                    () => {
                        this.reevaluateDimension($protectingElement, component);
                    },
                    () => {
                        this.recenter($protectingElement);
                    }
                );

                // Reevaluate size now that the button pane is in place
                this.reevaluateDimension($protectingElement, component);

                component.onShow();
                component.onFocus();
            })
            .fail(possibleJqXhr => {
                if (ajaxErrorHelper.isjqXhrObject(possibleJqXhr)) {
                    if (ajaxErrorHelper.isConnectionProblem(possibleJqXhr)) {
                        component.myFlow.handleAjaxError(possibleJqXhr);
                    }
                }
                dfd.reject();
            });
        myFlow.addTask(loadingPromise);
        return dfd.promiseWithoutTask();
    }

    onClose<T>(
        dfd: JQueryDeferredPlus<T>,
        protectingElement: JQuery<HTMLElement>,
        loadingSubscriber: KnockoutSubscription,
        titleSubscriber: KnockoutSubscription,
        component: Component,
        sizeSubscriber: () => void
    ): void {
        component.myFlow.clearLocalNotification();
        this.dialogStack.remove(component);
        component.onHide();

        if (component.manifest.reuseInstance === false) {
            component.onDestroy();
        }

        $(window).off("resize", sizeSubscriber);

        protectingElement.dialog("destroy");
        ko.virtualElements.emptyNode(protectingElement[0]);

        loadingSubscriber.dispose();
        titleSubscriber.dispose();

        dfd.resolve();
    }

    close(dfd: JQueryDeferredPlus<any>, protectingElement: JQuery<HTMLElement>, data: any): void {
        dfd.resolve(data);
        protectingElement.dialog("close");
    }

    reevaluateDimension(protectingElement: JQuery<HTMLElement>, component: Component): void {
        const dimension = component.getDimensions(window.innerWidth, window.innerHeight);

        protectingElement.dialog("option", {
            width: dimension.width,
            height: dimension.height
        });

        protectingElement.dialog("option", "position", { my: "center", at: "center", of: window });

        component.onContainerSizeChanged();
    }

    recenter(protectingElement: JQuery<HTMLElement>): void {
        protectingElement.dialog("option", "position", { my: "center", at: "center", of: window });
    }

    showErrorDialog(myFlow: MyFlow, title: string, message: string, showCorrelationOnly: boolean): JQueryPromise<void> {
        showCorrelationOnly = typeof showCorrelationOnly !== "undefined" ? showCorrelationOnly : false;
        return this.showCustomDialog<void>(myFlow, config.componentIds.error, {
            title: title,
            text: message,
            showCorrelationOnly: showCorrelationOnly
        });
    }

    showInformationDialog(myFlow: MyFlow, title: string, message: string): JQueryPromise<void> {
        return this.showCustomDialog<void>(myFlow, config.componentIds.information, { title: title, text: message });
    }

    showConfirmDialog(
        myFlow: MyFlow,
        title: string,
        message: string,
        buttonText1: string,
        buttonText2: string,
        buttonText3: string
    ): JQueryPromise<boolean> {
        const buttonList = [buttonText1, buttonText2];
        if (buttonText3 !== undefined) {
            buttonList.push(buttonText3);
        }

        return this.showCustomDialog<boolean>(myFlow, config.componentIds.confirm, {
            title: title,
            text: message,
            buttons: buttonList
        });
    }
}

const dialog = new Dialog();
export default dialog;
